import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';

import { Button, Spinner } from '@/components/ui';
import { useReportContent } from '@/api/useReport';

import styles from './ReportDialog.module.css';

interface ReportDialogProps {
    isOpen: boolean;
    contentType: 'poll' | 'comment';
    contentId: number;
    onClose: () => void;
}

const REASONS = [
    { value: 'spam', label: 'Spam' },
    { value: 'inappropriate', label: 'Contenido inapropiado' },
    { value: 'harassment', label: 'Acoso' },
    { value: 'misinformation', label: 'Desinformacion' },
    { value: 'other', label: 'Otro' },
] as const;

export function ReportDialog({ isOpen, contentType, contentId, onClose }: ReportDialogProps) {
    const [reason, setReason] = useState('');
    const [description, setDescription] = useState('');
    const reportMutation = useReportContent();

    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
            }
        },
        [onClose]
    );

    useEffect(() => {
        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
            return () => document.removeEventListener('keydown', handleKeyDown);
        }
    }, [isOpen, handleKeyDown]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!reason) {
            toast.error('Selecciona un motivo');
            return;
        }
        reportMutation.mutate(
            { contentType, contentId, reason, description: description.trim() || undefined },
            {
                onSuccess: () => {
                    toast.success('Reporte enviado. Gracias por ayudar.');
                    setReason('');
                    setDescription('');
                    onClose();
                },
                onError: () => {
                    toast.error('No se pudo enviar el reporte. Intenta de nuevo.');
                },
            }
        );
    };

    if (!isOpen) return null;

    return (
        <div className={styles.overlay} onClick={onClose} role="dialog" aria-modal="true" aria-label="Reportar contenido">
            <div className={styles.card} onClick={(e) => e.stopPropagation()}>
                <h3 className={styles.title}>Reportar contenido</h3>
                <form onSubmit={handleSubmit} className={styles.form}>
                    <fieldset className={styles.reasons}>
                        <legend className={styles.legend}>Motivo del reporte</legend>
                        {REASONS.map((r) => (
                            <label key={r.value} className={styles.radioLabel}>
                                <input
                                    type="radio"
                                    name="reason"
                                    value={r.value}
                                    checked={reason === r.value}
                                    onChange={() => setReason(r.value)}
                                    className={styles.radio}
                                />
                                <span>{r.label}</span>
                            </label>
                        ))}
                    </fieldset>

                    <div className={styles.descriptionGroup}>
                        <label htmlFor="report-description" className={styles.label}>
                            Descripcion (opcional)
                        </label>
                        <textarea
                            id="report-description"
                            className={styles.textarea}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Describe el problema..."
                            maxLength={500}
                            rows={3}
                        />
                    </div>

                    <div className={styles.actions}>
                        <Button variant="secondary" size="sm" onClick={onClose} type="button">
                            Cancelar
                        </Button>
                        <Button
                            variant="danger"
                            size="sm"
                            type="submit"
                            disabled={!reason || reportMutation.isPending}
                        >
                            <span style={{ visibility: reportMutation.isPending ? 'hidden' : 'visible' }}>Enviar reporte</span>
                            {reportMutation.isPending && <Spinner size="sm" variant="inverse" className={styles.btnSpinner} />}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
}
